import currency from "currency.js";
import { useState } from "react";
import { Link } from "react-router-dom";
import { useAccount } from "../../../hooks/api/admin/useAccount";
import { useAccountFamily } from "../../../hooks/api/admin/useSubaccounts";
import { IAevoPosition, ICollateral } from "../../../interfaces/aevo";
import { formatAmount, getProfitTextColor, shortenAddress } from "../../../utils/strings";
import { isSameAddress, subaccountLabel, useAccountLabel } from "../../shared/SubaccountIndicator";
import {
  IAccountTotals,
  accountTotals,
  addTotals,
  EMPTY_TOTALS,
  formatUsd,
  pluralize,
  sortSubaccounts,
} from "../../shared/SubaccountIndicator/utils";
import {
  AssetsText,
  Caret,
  EmptyText,
  FamilyHeader,
  FamilyWrapper,
  MemberCell,
  MemberHeaderRow,
  MemberName,
  MemberRow,
  PositionRow,
  PositionsTable,
  RoleTag,
  StatusText,
  TextButton,
  TotalsRow,
} from "./style";

interface IFamilyMember {
  address: string;
  name: string;
  isMaster: boolean;
  archived: boolean;
  balances: ICollateral[];
  positions: IAevoPosition[];
  totals: IAccountTotals;
}

const formatPrice = (value?: string) => (value && Number(value) ? currency(value).format() : "-");

const assetsSummary = (balances: ICollateral[]) =>
  balances
    .filter((b) => Number(b.balance))
    .map((b) => `${formatAmount(Number(b.balance))} ${b.collateral_asset}`)
    .join(" · ") || "-";

function MemberPositions({ positions }: { positions: IAevoPosition[] }) {
  if (!positions.length) {
    return (
      <PositionsTable>
        <EmptyText>No open positions</EmptyText>
      </PositionsTable>
    );
  }

  return (
    <PositionsTable>
      <PositionRow isHeader>
        <span>Instrument</span>
        <span>Side</span>
        <span>Contracts</span>
        <span>Entry</span>
        <span>Mark</span>
        <span>uPnL</span>
        <span>Liq. price</span>
        <span>Margin</span>
      </PositionRow>
      {positions.map((p) => (
        <PositionRow key={p.instrument_id}>
          <span>{p.instrument_name}</span>
          <span style={{ color: getProfitTextColor(p.side === "buy" ? 1 : -1) }}>
            {p.side === "buy" ? "Long" : "Short"}
          </span>
          <span>{Number(p.amount).toLocaleString()}</span>
          <span>{formatPrice(p.avg_entry_price)}</span>
          <span>{formatPrice(p.mark_price)}</span>
          <span style={{ color: getProfitTextColor(Number(p.unrealized_pnl)) }}>
            {currency(p.unrealized_pnl).format()}
          </span>
          <span>{formatPrice(p.liquidation_price)}</span>
          <span>{p.margin_type === "ISOLATED" ? "Isolated" : "Cross"}</span>
        </PositionRow>
      ))}
    </PositionsTable>
  );
}

function TotalsCells({ totals }: { totals: IAccountTotals }) {
  return (
    <>
      <span>{currency(totals.collateral).format()}</span>
      <span style={{ color: getProfitTextColor(totals.upnl) }}>{currency(totals.upnl).format()}</span>
      <span>{currency(totals.maintenanceMargin).format()}</span>
      <span>{totals.positions}</span>
    </>
  );
}

/**
 * Account page "Family" tab: the master and all of its subaccounts in one table, with totals and
 * each account's open positions, so ops can watch a whole family without switching accounts.
 */
export function AccountFamily({ account }: { account?: string }) {
  const getLabel = useAccountLabel();
  const { master, subaccounts, isMaster, hasFamily } = useAccountFamily(account);
  // Reuse the page's own account request when the master is the open account
  const { data: masterData } = useAccount(isMaster ? account : master);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  if (!hasFamily || !master) {
    return (
      <FamilyWrapper>
        <FamilyHeader>
          <span>Account family</span>
        </FamilyHeader>
        <PositionsTable>
          <EmptyText>This account has no master account or subaccounts.</EmptyText>
        </PositionsTable>
      </FamilyWrapper>
    );
  }

  const members: IFamilyMember[] = [
    {
      address: master,
      name: getLabel(master),
      isMaster: true,
      archived: false,
      balances: masterData?.collaterals ?? [],
      positions: masterData?.positions ?? [],
      totals: accountTotals(masterData?.collaterals, masterData?.positions),
    },
    ...sortSubaccounts(subaccounts).map((sub) => ({
      address: sub.subaccount,
      name: subaccountLabel(sub),
      isMaster: false,
      archived: sub.archived,
      balances: sub.balances,
      positions: sub.positions,
      totals: accountTotals(sub.balances, sub.positions),
    })),
  ];
  const familyTotals = members.reduce((sum, m) => addTotals(sum, m.totals), EMPTY_TOTALS);
  const allCollapsed = members.every((m) => collapsed.has(m.address));

  const toggle = (address: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(address)) next.delete(address);
      else next.add(address);
      return next;
    });

  return (
    <FamilyWrapper>
      <FamilyHeader>
        <span>
          Account family · master + {pluralize(subaccounts.length, "subaccount")} · {formatUsd(familyTotals.collateral)}{" "}
          collateral
        </span>
        <TextButton onClick={() => setCollapsed(allCollapsed ? new Set() : new Set(members.map((m) => m.address)))}>
          {allCollapsed ? "Expand all" : "Collapse all"}
        </TextButton>
      </FamilyHeader>
      <MemberHeaderRow>
        <span>Account</span>
        <span>Status</span>
        <span>Collateral</span>
        <span>uPnL</span>
        <span>Maint. margin</span>
        <span>Positions</span>
        <span>Assets</span>
      </MemberHeaderRow>
      <TotalsRow>
        <span>Family total</span>
        <span />
        <TotalsCells totals={familyTotals} />
        <span />
      </TotalsRow>
      {members.map((member) => {
        const isOpen = !collapsed.has(member.address);
        const isCurrent = isSameAddress(member.address, account);
        return (
          <div key={member.address}>
            <MemberRow
              isCurrent={isCurrent}
              isArchived={member.archived}
              role="button"
              tabIndex={0}
              aria-expanded={isOpen}
              onClick={() => toggle(member.address)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  toggle(member.address);
                }
              }}
            >
              <MemberCell>
                <Caret isOpen={isOpen}>▸</Caret>
                <RoleTag variant={member.isMaster ? "master" : "sub"}>{member.isMaster ? "Master" : "Sub"}</RoleTag>
                <MemberName>
                  <Link to={`/account/${member.address}/positions`} onClick={(e) => e.stopPropagation()}>
                    {member.name}
                  </Link>
                  <span>{shortenAddress(member.address)}</span>
                </MemberName>
                {isCurrent ? <RoleTag variant="current">Viewing</RoleTag> : null}
              </MemberCell>
              <StatusText isArchived={member.archived}>{member.archived ? "Archived" : "Active"}</StatusText>
              {member.isMaster && !masterData ? (
                <>
                  <span>-</span>
                  <span>-</span>
                  <span>-</span>
                  <span>-</span>
                </>
              ) : (
                <TotalsCells totals={member.totals} />
              )}
              <AssetsText>{assetsSummary(member.balances)}</AssetsText>
            </MemberRow>
            {isOpen ? <MemberPositions positions={member.positions} /> : null}
          </div>
        );
      })}
    </FamilyWrapper>
  );
}
